import React, { useState } from "react";
import { api } from "@/lib/api";
import { RefreshCw, ShieldCheck, AlertTriangle, Sparkles } from "lucide-react";

/**
 * Owner-only inline cleanup for the analytics DB.
 * Wraps POST /api/admin/analytics/reclassify with a two-step confirmation:
 *   1) Dry run — shows how many events WILL be flipped from human → bot
 *   2) Apply — executes the same operation and rebuilds `pageview_daily`
 *
 * Uses the shared `api` axios client so the Authorization header is
 * attached automatically from `localStorage.propertic_token` — no
 * DevTools / curl / manual token juggling needed.
 */
export default function AnalyticsCleanup({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);
  const [error, setError] = useState("");
  const [days, setDays] = useState(28);

  const run = async (dryRun) => {
    setBusy(true);
    setError("");
    try {
      const res = await api.post(
        `/admin/analytics/reclassify?days=${days}&dry_run=${dryRun}`
      );
      if (dryRun) {
        setPreview(res.data);
        setApplied(null);
      } else {
        setApplied(res.data);
        setPreview(null);
        if (onDone) onDone();
      }
    } catch (e) {
      setError(
        e?.response?.data?.detail || e?.message || "Cleanup failed. Check console."
      );
    } finally {
      setBusy(false);
    }
  };

  const summary = preview || applied;

  return (
    <section
      className="bg-white rounded-2xl border hairline p-5 sm:p-6 mb-8"
      data-testid="analytics-cleanup-card"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-propertic-gold" />
          <h3 className="font-editorial text-base sm:text-lg text-propertic-navy">
            Analytics cleanup
          </h3>
        </div>
        <span className="font-mono-brand text-[10px] tracking-[0.14em] uppercase text-propertic-muted">
          Owner only · idempotent
        </span>
      </div>

      <p className="text-[13px] text-propertic-ink-2/95 leading-relaxed mb-4 max-w-3xl">
        Re-runs bot detection over the last{" "}
        <span className="font-mono-brand text-propertic-navy">{days}</span> days
        of pageview events using the latest UA regex + browser-token backstop,
        then rebuilds daily counters so the summary cards on this page reflect
        real humans only. Safe to run multiple times — a second run on the same
        window reports zero flips.
      </p>

      {/* Window picker */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="font-mono-brand text-[10px] tracking-[0.14em] uppercase text-propertic-muted">
          Window
        </span>
        {[7, 14, 28, 60, 90].map((d) => (
          <button
            key={d}
            type="button"
            data-testid={`cleanup-window-${d}`}
            onClick={() => setDays(d)}
            disabled={busy}
            className={`px-3 py-1 rounded-full text-xs font-mono-brand transition-colors border hairline ${
              days === d
                ? "bg-propertic-navy text-propertic-cream border-propertic-navy"
                : "bg-white text-propertic-navy hover:bg-propertic-cream"
            } ${busy ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {d}d
          </button>
        ))}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 mb-4">
        <button
          type="button"
          onClick={() => run(true)}
          disabled={busy}
          data-testid="cleanup-dry-run"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border hairline text-propertic-navy text-sm hover:bg-propertic-cream disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          <ShieldCheck className="w-4 h-4" />
          {busy && preview === null && applied === null ? "Running…" : "Dry run"}
        </button>
        <button
          type="button"
          onClick={() => run(false)}
          disabled={busy}
          data-testid="cleanup-apply"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-propertic-navy text-propertic-cream text-sm hover:bg-propertic-navy-2 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
          {busy ? "Working…" : "Apply cleanup"}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div
          className="rounded-xl border hairline bg-red-50 text-red-800 p-3 text-sm mb-4 flex items-start gap-2"
          data-testid="cleanup-error"
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Result */}
      {summary && (
        <div
          className="rounded-2xl border hairline bg-propertic-sand/40 p-4 sm:p-5"
          data-testid={preview ? "cleanup-preview" : "cleanup-applied"}
        >
          <div className="flex items-baseline justify-between gap-2 mb-3">
            <p className="font-mono-brand text-[10px] tracking-[0.14em] uppercase text-propertic-gold">
              {preview ? "Dry-run preview" : "Applied · dashboard refreshed"}
            </p>
            <p className="font-mono-brand text-[10px] text-propertic-muted tabular-nums">
              {summary.range_start} → {summary.range_end}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat
              label="Scanned"
              value={summary.scanned}
              testid="cleanup-scanned"
            />
            <Stat
              label="Flipped → bot"
              value={summary.flipped_to_bot}
              testid="cleanup-flipped-bot"
              highlight={summary.flipped_to_bot > 0}
            />
            <Stat
              label="Flipped → human"
              value={summary.flipped_to_human}
              testid="cleanup-flipped-human"
            />
            <Stat
              label="Already correct"
              value={summary.already_correct}
              testid="cleanup-correct"
            />
          </div>
          {applied?.rollup?.days_rebuilt != null && (
            <p className="mt-3 font-mono-brand text-[11px] text-propertic-muted">
              Daily rollup rebuilt · {applied.rollup.days_rebuilt} day(s)
            </p>
          )}
          {preview && summary.sample_flipped?.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <p className="font-mono-brand text-[10px] tracking-[0.14em] uppercase text-propertic-muted mb-2">
                Sample flips (first {summary.sample_flipped.length})
              </p>
              <table className="w-full text-[12px]">
                <thead className="text-propertic-muted">
                  <tr>
                    <th className="text-start pb-1">Day</th>
                    <th className="text-start pb-1">Was</th>
                    <th className="text-start pb-1">Now</th>
                    <th className="text-start pb-1">User agent</th>
                  </tr>
                </thead>
                <tbody className="divide-y hairline">
                  {summary.sample_flipped.slice(0, 8).map((s, i) => (
                    <tr key={i}>
                      <td className="py-1.5 font-mono-brand text-propertic-muted whitespace-nowrap tabular-nums">
                        {s.day}
                      </td>
                      <td className="py-1.5 text-propertic-navy whitespace-nowrap">
                        {s.from.label}
                      </td>
                      <td className="py-1.5 text-propertic-navy whitespace-nowrap">
                        {s.to.label}
                      </td>
                      <td className="py-1.5 text-propertic-muted truncate max-w-[280px]">
                        {s.ua}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, testid, highlight }) {
  return (
    <div className="rounded-xl bg-white border hairline p-3" data-testid={testid}>
      <p className="font-mono-brand text-[9px] tracking-[0.14em] uppercase text-propertic-muted mb-1">
        {label}
      </p>
      <p
        className={`font-editorial text-xl tabular-nums ${
          highlight ? "text-propertic-gold" : "text-propertic-navy"
        }`}
      >
        {(value ?? 0).toLocaleString()}
      </p>
    </div>
  );
}
